import React, { useMemo, useRef, useState } from 'react';
import { ArrowRight, CalculatorIcon, CompassIcon, StarIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MaterialCatalogCard } from './MaterialCatalogCard';
import { categories, type Product } from '../data/products';
type CatalogFilter = 'popular' | 'all' | string;
type CatalogProduct = Product & {
  categoryName: string;
};
const TOP_SELLER_SLUGS = new Set(['5-8-minus', '1-25-minus', '5-8-clear', '1-5-drain-rock', '3-way-topsoil', 'medium-bark']);
export function Services() {
  const [selectedFilter, setSelectedFilter] = useState<CatalogFilter>('popular');
  const filterRailRef = useRef<HTMLDivElement>(null);
  const catalogProducts = useMemo<CatalogProduct[]>(() => categories.flatMap(category => category.products.map(product => ({
    ...product,
    categoryName: category.name
  }))), []);
  const visibleProducts = useMemo(() => {
    if (selectedFilter === 'popular') {
      return catalogProducts.filter(product => TOP_SELLER_SLUGS.has(product.slug));
    }
    if (selectedFilter === 'all') {
      return catalogProducts;
    }
    return catalogProducts.filter(product => product.categoryName === selectedFilter);
  }, [catalogProducts, selectedFilter]);
  const groupedCatalog = useMemo(() => categories.map(category => ({
    category,
    products: catalogProducts.filter(product => product.categoryName === category.name)
  })).filter(group => group.products.length > 0), [catalogProducts]);
  const filters = useMemo(() => [{
    id: 'popular',
    label: 'Best Sellers',
    count: catalogProducts.filter(product => TOP_SELLER_SLUGS.has(product.slug)).length
  }, {
    id: 'all',
    label: 'All Materials',
    count: catalogProducts.length
  }, ...categories.map(category => ({
    id: category.name,
    label: category.name,
    count: category.products.length
  }))], [catalogProducts]);
  const selectFilter = (filterId: CatalogFilter) => {
    setSelectedFilter(filterId);
    window.requestAnimationFrame(() => {
      const rail = filterRailRef.current;
      const selectedChip = rail?.querySelector<HTMLElement>(`[data-filter-id="${filterId}"]`);
      if (!rail || !selectedChip) return;
      const railBounds = rail.getBoundingClientRect();
      const chipBounds = selectedChip.getBoundingClientRect();
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      const isPartiallyHidden = chipBounds.right > railBounds.right || chipBounds.left < railBounds.left;
      const isMobile = window.matchMedia('(max-width: 767px)').matches;

      // On mobile, a tap on a cut-off category pins that exact pill to the
      // left edge. The rail itself remains natively swipeable both ways.
      if (isMobile && isPartiallyHidden) {
        rail.scrollTo({
          left: selectedChip.offsetLeft,
          behavior
        });
      } else if (chipBounds.right > railBounds.right) {
        rail.scrollBy({
          left: chipBounds.right - railBounds.right + 12,
          behavior
        });
      } else if (chipBounds.left < railBounds.left) {
        rail.scrollBy({
          left: chipBounds.left - railBounds.left - 12,
          behavior
        });
      }
    });
  };
  return <section className="relative overflow-hidden bg-[#f1f4f0] py-20 transition-colors duration-300 dark:bg-[#131413] md:py-24" id="materials" data-id="element-1626" data-mp="39gjk0ikue6ry">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#e8e0d2]/20 to-transparent dark:from-[#3a3633]/20" data-id="element-1627" data-mp="2bum5sxdhwnlt" />
      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8" data-id="element-1628" data-mp="2lqfpd0pmthd3">
        <div className="mb-10 md:mb-12" data-id="element-1629" data-mp="2psaqo57kbuqv">
          <motion.div initial={{
          width: 0,
          opacity: 0
        }} whileInView={{
          width: 64,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.65,
          ease: [0.22, 1, 0.36, 1]
        }} className="mb-7 h-1 rounded-full bg-brand-orange" data-id="element-1630" data-mp="2ptc4ghfyqvca" />
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-baseline md:gap-7" data-id="element-1631" data-mp="3mz4hfep3f5o">
            <motion.h2 initial={{
            opacity: 0,
            y: 18
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20
          }} className="font-heading text-5xl font-extrabold leading-[1.1] tracking-tight text-brand-charcoal dark:text-white md:text-6xl lg:text-7xl" data-id="element-1632" data-mp="3ut0le4lcd2z8">
              Materials <span className="font-black text-[#04A05C]" data-id="element-1633" data-mp="w8igw0b1akm8">Hub</span>
            </motion.h2>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#17A348]" data-id="element-1634" data-mp="ra79dw87om76">— Browse by material</span>
          </div>
          <p className="max-w-2xl font-sub text-lg font-medium leading-relaxed tracking-wide text-brand-charcoal dark:text-white" data-id="element-1635" data-mp="15arux05igw4d">
            Start with the materials customers order most, then browse by category. Every item opens a full product page with specs, delivery details, and ordering.
          </p>
        </div>

        <div className="mb-8" role="tablist" aria-label="Material categories" data-id="element-1636" data-mp="1otnysqcigcql">
          <div ref={filterRailRef} className="flex gap-2 overflow-x-auto snap-x snap-mandatory scroll-smooth scrollbar-hide touch-pan-x [-webkit-overflow-scrolling:touch]" data-id="element-1637" data-mp="1avzdxiig15lj">
            {filters.map(filter => {
            const isActive = selectedFilter === filter.id;
            return <button key={filter.id} data-filter-id={filter.id} type="button" role="tab" aria-selected={isActive} onClick={() => selectFilter(filter.id)} className={`snap-start shrink-0 whitespace-nowrap rounded-full border px-4 py-2.5 text-sm font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#131413] ${isActive ? 'border-[#04A05C] bg-[#04A05C] text-white dark:border-[#04A05C] dark:bg-[#04A05C] dark:text-[#102016]' : 'border-stone-200 bg-white/70 text-stone-800 hover:border-[#04A05C]/60 hover:bg-white dark:border-white/15 dark:bg-white/[0.06] dark:text-white/80 dark:hover:border-[#22c95c]/70 dark:hover:bg-white/[0.12]'}`} data-id="element-1638" data-mp="15xxuuxl0p6a2">
                {filter.id === 'popular' && <StarIcon className="mr-1.5 inline-block h-3.5 w-3.5 -mt-0.5 fill-current" aria-hidden="true" data-id="element-1639" data-mp="2b4z2zp88ce8t" />}
                {filter.label}
                <span className={`ml-1.5 text-xs ${isActive ? 'text-white/70 dark:text-[#102016]/65' : 'text-stone-500 dark:text-white/50'}`} data-id="element-1640" data-mp="37u6fk7qvfd0l">{filter.count}</span>
              </button>;
          })}
          </div>
        </div>

        {selectedFilter === 'all' ? <div className="space-y-12" role="tabpanel" aria-label="All materials by category" data-id="element-1641" data-mp="2tvgfcj8r8x80">
            {groupedCatalog.map(group => <section key={group.category.name} aria-labelledby={`category-${group.category.name}`} className="border-t border-stone-300/70 pt-8 first:border-t-0 first:pt-0 dark:border-white/15" data-id="element-1642" data-mp="1nfw8hgshyqww">
                <div className="mb-5 flex items-baseline justify-between gap-4" data-id="element-1643" data-mp="18cw88rg2o62u">
                  <div data-id="element-1644" data-mp="1daeh6hu3alau">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#05894e] dark:text-[#4ed491]" data-id="element-1645" data-mp="1dbpq0w2e6j8t">Material category</p>
                    <h3 id={`category-${group.category.name}`} className="mt-1 font-heading text-2xl font-bold text-stone-900 dark:text-white" data-id="element-1646" data-mp="1i86a9f6h0dgm">{group.category.name}</h3>
                  </div>
                  <span className="text-sm font-semibold text-stone-500 dark:text-white/50" data-id="element-1647" data-mp="1r4p2ep6vnxc5">{group.products.length} {group.products.length === 1 ? 'item' : 'items'}</span>
                </div>
                <motion.div layout className="grid grid-cols-1 gap-[22px] sm:grid-cols-2 sm:gap-[26px] md:grid-cols-3" data-id="element-1648" data-mp="20yfndk3sppn3">
                  {group.products.map((product, index) => <MaterialCatalogCard key={product.slug} product={product} categoryName={group.category.name} isBestSeller={TOP_SELLER_SLUGS.has(product.slug)} index={index} data-id="element-1649" data-mp="6nj0wjvv74or" data-mpi={index} />)}
                </motion.div>
              </section>)}
          </div> : <motion.div layout className="grid grid-cols-1 gap-[22px] sm:grid-cols-2 sm:gap-[26px] md:grid-cols-3" role="tabpanel" aria-label={`${filters.find(filter => filter.id === selectedFilter)?.label ?? 'Materials'} materials`} data-id="element-1650" data-mp="2dh1jly36aveq">
            {visibleProducts.map((product, index) => <MaterialCatalogCard key={product.slug} product={product} categoryName={product.categoryName} isBestSeller={TOP_SELLER_SLUGS.has(product.slug)} index={index} data-id="element-1651" data-mp="2ib5u2rbndlkk" data-mpi={index} />)}
          </motion.div>}

        {visibleProducts.length === 0 && selectedFilter !== 'all' && <div className="rounded-2xl border border-dashed border-stone-300 px-6 py-12 text-center text-stone-600 dark:border-white/15 dark:text-white/65" data-id="element-1652" data-mp="23kottbar11p9">
            No materials are available in this category yet.
          </div>}

        <div className="mt-10 grid gap-4 md:grid-cols-2" data-id="element-1653" data-mp="3eysjw6e7u5p1">
          <Link to="/calculator" className="group flex items-center gap-4 rounded-2xl border border-stone-200 bg-white/55 p-5 transition-colors hover:border-[#04A05C]/50 hover:bg-white dark:border-white/10 dark:bg-white/[0.05] dark:hover:bg-white/[0.08]" data-id="element-1654" data-mp="3ays5umv13wii">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#04A05C]/10 text-[#05894e] dark:bg-[#04A05C]/15 dark:text-[#4ed491]" data-id="element-1655" data-mp="3nip1kogtjnvg"><CalculatorIcon className="h-5 w-5" data-id="element-1656" data-mp="3diervdtzsyl1" /></span>
            <span className="min-w-0 flex-1" data-id="element-1657" data-mp="3dl0xv918j615"><span className="block font-bold text-stone-900 dark:text-white" data-id="element-1658" data-mp="53el88tx2sqc">Not sure how much to order?</span><span className="mt-0.5 block text-sm text-stone-600 dark:text-white/60" data-id="element-1659" data-mp="a1y605wrxyh4">Calculate your project in yards.</span></span>
            <ArrowRight className="h-5 w-5 shrink-0 text-stone-400 transition-transform group-hover:translate-x-1 group-hover:text-[#05894e] dark:text-white/40" data-id="element-1660" data-mp="3rho8ufq0vwq2" />
          </Link>
          <Link to="/help" className="group flex items-center gap-4 rounded-2xl border border-stone-200 bg-white/55 p-5 transition-colors hover:border-[#04A05C]/50 hover:bg-white dark:border-white/10 dark:bg-white/[0.05] dark:hover:bg-white/[0.08]" data-id="element-1661" data-mp="o1h220cb9jd2">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#04A05C]/10 text-[#05894e] dark:bg-[#04A05C]/15 dark:text-[#4ed491]" data-id="element-1662" data-mp="b30bubtt65d9"><CompassIcon className="h-5 w-5" data-id="element-1663" data-mp="14u8dpscsr6a" /></span>
            <span className="min-w-0 flex-1" data-id="element-1664" data-mp="165ehw99ub0k"><span className="block font-bold text-stone-900 dark:text-white" data-id="element-1665" data-mp="62matdqmjvoq">Need help choosing?</span><span className="mt-0.5 block text-sm text-stone-600 dark:text-white/60" data-id="element-1666" data-mp="3njvrz8lyxuwt">Answer a few questions for a recommendation.</span></span>
            <ArrowRight className="h-5 w-5 shrink-0 text-stone-400 transition-transform group-hover:translate-x-1 group-hover:text-[#05894e] dark:text-white/40" data-id="element-1667" data-mp="3sb5e3z8b6g3y" />
          </Link>
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-5 rounded-2xl border border-stone-200/70 px-6 py-5 text-center sm:flex-row sm:text-left dark:border-white/10" data-id="element-1668" data-mp="3jitpmiwr92qm">
          <div data-id="element-1669" data-mp="39it2elvrm2h5">
            <p className="font-sub text-lg text-stone-800 dark:text-white" data-id="element-1670" data-mp="39ijdz2qdc3dk">Can’t find what you’re looking for?</p>
            <p className="mt-1 text-sm text-stone-600 dark:text-white/60" data-id="element-1671" data-mp="2pycfa27wipcd">We source materials beyond this catalog—tell us what you need.</p>
          </div>
          <Link to="/contact" className="inline-flex shrink-0 items-center gap-2 rounded-full bg-brand-button px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-buttonHover" data-id="element-1672" data-mp="2zwrwndh4mk8o">
            Contact Us <ArrowRight className="h-4 w-4" data-id="element-1673" data-mp="38w58gcsq3kwk" />
          </Link>
        </div>
      </div>
    </section>;
}